import React, { useState, useEffect, useCallback } from 'react';
import Timer from './Timer';
// WorkoutCard might not be directly used, or parts of its styling/logic could be adapted.
// import WorkoutCard from './WorkoutCard'; 

const ESTIMATED_DURATION_FOR_REP_EXERCISE = 60; // 60 seconds

const WorkoutScreen = ({ workout, onBack, overallTimeLeftProp, effectiveTotalDurationProp, isOverallPaused, setOverallIsPaused, onActualDurationCalculated, onSetOverallTimeLeft }) => {
  const [allExercisesWithTimes, setAllExercisesWithTimes] = useState([]);
  const [currentGlobalExerciseIndex, setCurrentGlobalExerciseIndex] = useState(0);
  const [currentExerciseRemainingTime, setCurrentExerciseRemainingTime] = useState(0);

  // Phase 1: Build the flat list of exercises with cumulative timings
  useEffect(() => {
    console.log("Phase 1 Effect: Building exercise list for workout:", workout?.id);
    const flatList = [];
    let cumulativeTime = 0;

    const addExercisesToList = (phaseName, exercises, rounds = 1, phaseType) => {
      for (let r = 1; r <= rounds; r++) {
        for (const ex of exercises) {
          let displayDuration;
          if (phaseType === 'circuit' && workout.id === 4) {
            displayDuration = workout.circuit.durationPerExercise;
          } else if (ex.duration) {
            displayDuration = ex.duration;
          } else {
            displayDuration = ESTIMATED_DURATION_FOR_REP_EXERCISE;
          }
          
          const startTime = cumulativeTime;
          cumulativeTime += displayDuration;
          const endTime = cumulativeTime;

          flatList.push({
            ...ex,
            phaseName,
            roundNumber: rounds > 1 ? r : null,
            actualDuration: displayDuration,
            cumulativeStartTime: startTime,
            cumulativeEndTime: endTime,
            isTimed: phaseType === 'circuit' && workout.id === 4 ? true : !!ex.duration,
            uniqueKey: `${phaseName}-${ex.name}-${r}-${startTime}-${Math.random()}`
          });
        }
      }
    };

    addExercisesToList('Warm-Up', workout.warmUp.exercises, 1, 'warmup');
    addExercisesToList('Circuit', workout.circuit.exercises, workout.circuit.rounds, 'circuit');
    addExercisesToList('Finisher', workout.finisher.exercises, workout.finisher.rounds, 'finisher');
    
    const cooldownStartTime = cumulativeTime;
    const cooldownDuration = workout.coolDown.duration;
    cumulativeTime += cooldownDuration;
    flatList.push({
        name: workout.coolDown.details,
        phaseName: 'Cooldown',
        roundNumber: null,
        actualDuration: cooldownDuration,
        cumulativeStartTime: cooldownStartTime,
        cumulativeEndTime: cumulativeTime,
        isTimed: true,
        uniqueKey: `cooldown-${cooldownStartTime}-${Math.random()}`
    });
    setAllExercisesWithTimes(flatList);

    if (onActualDurationCalculated) {
      onActualDurationCalculated(cumulativeTime);
    }
    console.log("Phase 1 Effect: List built, exercises count:", flatList.length);

  }, [workout, onActualDurationCalculated]);

  // Phase 2: Synchronize current exercise based on overallTimeLeftProp
  useEffect(() => {
    console.log(`Phase 2 Effect: overallTimeLeftProp: ${overallTimeLeftProp}, effectiveTotalDurationProp: ${effectiveTotalDurationProp}, currentGlobalIndex: ${currentGlobalExerciseIndex}, exercises: ${allExercisesWithTimes.length}`);
    if (!allExercisesWithTimes.length || overallTimeLeftProp === undefined || effectiveTotalDurationProp === undefined || effectiveTotalDurationProp <= 0) {
        console.log("Phase 2 Effect: Bailing out - insufficient data for sync.", { 
            hasExercises: allExercisesWithTimes.length > 0,
            overallTimeLeftProp,
            effectiveTotalDurationProp
        });
        // If effectiveTotalDurationProp is 0 or undefined, we can't calculate elapsed time reliably yet.
        // This might happen briefly when a workout is first selected before App.js sets effectiveTotalDurationForWorkout.
        // In such a case, ensure we default to the first exercise if the list is ready.
        if (allExercisesWithTimes.length > 0 && currentGlobalExerciseIndex !== 0 && effectiveTotalDurationProp === 0) {
            // This condition specifically targets the initial load scenario where we want to force to index 0
            // if other props are not yet ready for a full sync. But only if index isn't already 0.
            // setCurrentGlobalExerciseIndex(0); // Re-evaluating if this is needed or if App.js ensures correct init.
            // For now, let it pass and assume App.js initializes overallTimeLeft correctly for elapsedTime=0.
        }
        return;
    }

    const elapsedTime = effectiveTotalDurationProp - overallTimeLeftProp;
    console.log(`Phase 2 Effect: effectiveTotalDurationProp: ${effectiveTotalDurationProp}, elapsedTime: ${elapsedTime}`);

    let foundIndex = -1;
    if (elapsedTime <= allExercisesWithTimes[0].cumulativeStartTime + 0.5) { // Add small tolerance for elapsedTime almost 0
        foundIndex = 0;
    } else {
        for (let i = 0; i < allExercisesWithTimes.length; i++) {
            const ex = allExercisesWithTimes[i];
            if (elapsedTime >= ex.cumulativeStartTime && elapsedTime < ex.cumulativeEndTime) {
                foundIndex = i;
                break;
            }
        }
    }
    
    if (foundIndex === -1 && elapsedTime >= allExercisesWithTimes[allExercisesWithTimes.length - 1].cumulativeEndTime) {
        foundIndex = allExercisesWithTimes.length - 1;
        console.log("Phase 2 Effect: Elapsed time beyond last exercise, setting to last index:", foundIndex);
    } else if (foundIndex === -1) {
        console.warn("Phase 2 Effect: foundIndex is -1 unexpectedly. Defaulting to current index:", currentGlobalExerciseIndex);
        foundIndex = currentGlobalExerciseIndex; // Keep current index if no specific match to avoid jump to 0
    }

    console.log("Phase 2 Effect: Calculated foundIndex:", foundIndex);
    if (foundIndex !== currentGlobalExerciseIndex) {
      console.log("Phase 2 Effect: Updating currentGlobalExerciseIndex from", currentGlobalExerciseIndex, "to", foundIndex);
      setCurrentGlobalExerciseIndex(foundIndex);
    }

    if (foundIndex >= 0 && foundIndex < allExercisesWithTimes.length) {
      const currentEx = allExercisesWithTimes[foundIndex];
      const remainingForThisExercise = currentEx.cumulativeEndTime - elapsedTime;
      // Ensure remaining time is not negative and not greater than the exercise's actual duration
      const validatedRemaining = Math.min(currentEx.actualDuration, Math.max(0, Math.round(remainingForThisExercise)));
      setCurrentExerciseRemainingTime(validatedRemaining);
      console.log(`Phase 2 Effect: Set currentExerciseRemainingTime: ${validatedRemaining} for index ${foundIndex}`);
    } else if (elapsedTime >= effectiveTotalDurationProp && allExercisesWithTimes.length > 0) {
        setCurrentExerciseRemainingTime(0);
        if (!isOverallPaused && setOverallIsPaused) setOverallIsPaused(true);
        console.log("Phase 2 Effect: Workout finished based on elapsed time.");
    } else {
        setCurrentExerciseRemainingTime(0);
        console.log("Phase 2 Effect: Defaulted currentExerciseRemainingTime to 0.");
    }

  }, [overallTimeLeftProp, effectiveTotalDurationProp, allExercisesWithTimes, currentGlobalExerciseIndex, isOverallPaused, setOverallIsPaused]);


  const handleIndividualExerciseComplete = useCallback(() => {
    const currentEx = allExercisesWithTimes[currentGlobalExerciseIndex];
    console.log("Individual exercise timer completed for:", currentEx?.name);
    if (currentGlobalExerciseIndex >= allExercisesWithTimes.length - 1) {
        if (!isOverallPaused && setOverallIsPaused) {
            setOverallIsPaused(true); 
        }
    }
    // The main sync effect should handle advancing to the next exercise when overallTimeLeftProp updates.
  }, [currentGlobalExerciseIndex, allExercisesWithTimes, isOverallPaused, setOverallIsPaused]);

  const handleEndWorkoutEarlyClick = () => {
    if (workoutEffectivelyFinished) { 
        onBack();
    } else if (window.confirm("Are you sure you want to end the workout early?")) {
      if(setOverallIsPaused) setOverallIsPaused(true);
      onBack();
    }
  };

  const handleExerciseItemClick = (clickedExerciseIndex) => {
    if (!onSetOverallTimeLeft || effectiveTotalDurationProp === undefined || !allExercisesWithTimes[clickedExerciseIndex]) return;

    const clickedExercise = allExercisesWithTimes[clickedExerciseIndex];
    const targetElapsedTime = clickedExercise.cumulativeStartTime;
    const newOverallTimeLeft = effectiveTotalDurationProp - targetElapsedTime;
    
    console.log(`Clicked exercise ${clickedExercise.name}, teleporting to its start. New overallTimeLeft: ${newOverallTimeLeft}`);
    onSetOverallTimeLeft(newOverallTimeLeft);
    // The sync useEffect will handle updating currentGlobalExerciseIndex and currentExerciseRemainingTime
  };

  if (allExercisesWithTimes.length === 0) {
    return <div style={styles.loading}>Preparing your workout...</div>;
  }
  
  const currentExercise = allExercisesWithTimes[currentGlobalExerciseIndex];
  const workoutEffectivelyFinished = overallTimeLeftProp <= 0 || (currentGlobalExerciseIndex >= allExercisesWithTimes.length - 1 && currentExerciseRemainingTime <= 0);

  // Render logic for grouped exercises
  let lastPhaseRoundKey = null;
  const groupedExerciseItems = [];

  allExercisesWithTimes.forEach((ex, index) => {
    const phaseRoundKey = `${ex.phaseName}${ex.roundNumber || ''}`;
    if (phaseRoundKey !== lastPhaseRoundKey) {
      groupedExerciseItems.push(
        <li key={phaseRoundKey} style={styles.phaseHeader}>
          {ex.phaseName}{ex.roundNumber ? ` - Round ${ex.roundNumber}` : ''}
        </li>
      );
      lastPhaseRoundKey = phaseRoundKey;
    }

    const isCurrent = index === currentGlobalExerciseIndex && !workoutEffectivelyFinished;
    const elapsedTimeForCompletion = effectiveTotalDurationProp - overallTimeLeftProp;
    const isCompleted = ex.cumulativeEndTime <= elapsedTimeForCompletion && index !== currentGlobalExerciseIndex;
    const isLastAndFinished = index === allExercisesWithTimes.length -1 && workoutEffectivelyFinished;

    groupedExerciseItems.push(
      <li 
        key={ex.uniqueKey} 
        style={{
          ...styles.exerciseListItem,
          ...((isCurrent || isLastAndFinished) && styles.currentExerciseHighlight),
          ...(isCompleted && styles.completedExercise),
          cursor: 'pointer' // Add cursor pointer to indicate clickable
        }}
        onClick={() => handleExerciseItemClick(index)} // Add onClick handler
      >
        <div style={styles.exerciseListItemHeader}>
            <span style={styles.exNameInList}>{ex.name}</span>
            <span style={styles.exDurationInList}>({ex.isTimed ? `${ex.actualDuration}s` : `${ex.reps || 'N/A'} reps / ~${ex.actualDuration}s`})</span>
        </div>
        
        {(isCurrent || isLastAndFinished) && currentExercise && (
          <div style={styles.currentExerciseDetailsExpanded}>
            {ex.details && <p style={styles.expandedDetailsNotes}>Notes: {ex.details}</p>}
            {ex.reps && !ex.isTimed && <p style={styles.expandedDetailsReps}>Reps: {ex.reps}</p>}
            <div style={styles.expandedExerciseTimer}>
              <Timer
                key={currentExercise.uniqueKey} 
                duration={currentExerciseRemainingTime}
                onComplete={handleIndividualExerciseComplete} 
                onPause={() => {if(setOverallIsPaused) setOverallIsPaused(true);}} 
                onResume={() => {if(setOverallIsPaused) setOverallIsPaused(false);}}
                isPaused={isOverallPaused} 
                compact={true}
              />
            </div>
          </div>
        )}
      </li>
    );
  });

  return (
    <div style={styles.screen}>
      <div style={styles.fullExerciseListContainer}>
        <ul style={styles.exerciseListUl}>
          {groupedExerciseItems}
        </ul>
      </div>
      
      {workoutEffectivelyFinished && <h2 style={styles.workoutCompleteMessage}>Workout Complete! Well done!</h2>}
      
      <button onClick={handleEndWorkoutEarlyClick} style={styles.endWorkoutButton}>
        {workoutEffectivelyFinished ? 'Back to Menu' : 'End Workout Early'}
      </button>
    </div>
  );
};

const styles = {
  screen: {
    maxWidth: '700px',
    margin: '0 auto',
    fontFamily: 'sans-serif',
    marginTop: "50px" // User added, consider if still needed
  },
  loading: {
    textAlign: 'center',
    fontSize: '1.5em',
    padding: '50px',
    color: '#555',
  },
  fullExerciseListContainer: {
    padding: '10px', // Reduced padding a bit
  },
  exerciseListUl: {
    listStyleType: 'none',
    paddingLeft: 0,
  },
  phaseHeader: {
    fontSize: '1.2em',
    fontWeight: 'bold',
    color: '#0056b3', // Darker blue for phase headers
    marginTop: '20px',
    marginBottom: '10px',
    paddingBottom: '5px',
    borderBottom: '2px solid #007bff',
  },
  exerciseListItem: {
    padding: '10px 12px',
    border: '1px solid #e0e0e0',
    backgroundColor: '#fff',
    borderRadius: '6px',
    marginBottom: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    transition: 'all 0.3s ease',
  },
  exerciseListItemHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    // fontWeight: '500', // Moved to exNameInList
  },
  exNameInList: {
      fontWeight: '500',
      flexGrow: 1, // Allow name to take up space
      marginRight: '10px',
  },
  exDurationInList: {
      fontSize: '0.85em',
      color: '#555',
      whiteSpace: 'nowrap',
      width: "100px"
  },
  listItemPhase: { // No longer used directly in item header, part of phaseHeader style
    // fontWeight: 'normal',
    // fontSize: '0.9em',
    // color: '#555',
    // marginRight: '5px',
  },
  currentExerciseHighlight: {
    backgroundColor: '#e7f5ff',
    borderColor: '#007bff',
    boxShadow: '0 3px 8px rgba(0, 123, 255, 0.15)',
  },
  completedExercise: {
    opacity: 0.65, // Slightly more visible than before
    backgroundColor: '#f8f9fa',
    borderColor: '#eee',
  },
  currentExerciseDetailsExpanded: {
    marginTop: '10px',
    paddingTop: '10px',
    borderTop: '1px dashed #007bff',
  },
  expandedDetailsNotes: {
    fontSize: '0.9em',
    color: '#333',
    fontStyle: 'italic',
    margin: '5px 0',
  },
  expandedDetailsReps: {
    fontSize: '0.9em',
    color: '#333',
    margin: '5px 0',
    fontWeight: '500',
  },
  expandedExerciseTimer: {
    margin: '10px 0 5px 0',
  },
  listItemSubInfo: { // No longer used for non-current, info is in exDurationInList
    //   display: 'block',
    //   fontSize: '0.85em',
    //   color: '#777',
    //   marginTop: '4px',
  },
  workoutCompleteMessage: {
      textAlign: 'center',
      color: '#28a745',
      fontSize: '1.8em',
      margin: '20px 0',
  },
  endWorkoutButton: {
    display: 'block',
    width: 'fit-content',
    margin: '20px auto',
    padding: '12px 25px',
    fontSize: '1.1em',
    backgroundColor: '#dc3545',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
  },
};

export default WorkoutScreen; 